import { toCalendarYmd } from "@/features/opportunities/lib/set-date-window-display";

import type {
  PersonaOpportunitySetDetail,
  PersonaOpportunitySetRow,
} from "../api/persona-opportunity-sets.api";
import type {
  PersonaOpportunitySet,
  PersonaOpportunityTabCounts,
} from "../types/persona-opportunities.types";

/**
 * Set-level mapping: API rows → the view model the sets grid and workspace shell
 * render. Per-opportunity card/table rows are a separate concern (KTD9).
 */

/**
 * Display timestamp for set cards / header age.
 * READY uses `updatedAt` as generated-age until the API exposes `generationCompletedAt`.
 */
export function personaOpportunitySetDisplayTimestamp(set: {
  status: PersonaOpportunitySet["status"];
  createdAt: string;
  updatedAt: string;
}): string {
  return set.status === "READY" ? set.updatedAt : set.createdAt;
}

function storedText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function toSetBase(row: PersonaOpportunitySetRow) {
  const config = row.config;
  return {
    id: row.id,
    name: row.name,
    tenantId: row.tenantId,
    type:
      row.type === "COMPETITOR"
        ? ("COMPETITOR" as const)
        : row.type === "USER_GENERATED"
          ? ("USER_GENERATED" as const)
          : row.type === "POST_FLIGHT"
            ? ("POST_FLIGHT" as const)
            : ("PERSONA" as const),
    status: row.status,
    industry: storedText(config?.industry),
    benchmarkIndustry: storedText(config?.benchmarkIndustry),
    myBrand: storedText(config?.myBrand),
    mainCompetitor: storedText(config?.mainCompetitor),
    products: (row.products ?? []).filter((name) => name.trim().length > 0),
    personas: (row.personas ?? [])
      .map((persona) => persona.title.trim())
      .filter((title) => title.length > 0),
    industryCompetitors: config?.industryCompetitors ?? [],
    // v1 generates against a single platform even though the config stores a list.
    adPlatform: config?.adPlatforms?.[0] ?? null,
    generationStartedAt: storedText(row.generationStartedAt),
    generationCompletedAt: storedText(row.generationCompletedAt),
    startDate: toCalendarYmd(row.startDate),
    endDate: toCalendarYmd(row.endDate),
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

/** List row — tab counts stay `null`; footer totals come from Prisma counts. */
export function toPersonaOpportunitySet(row: PersonaOpportunitySetRow): PersonaOpportunitySet {
  return {
    ...toSetBase(row),
    opportunityCount: row.opportunityCount ?? 0,
    scriptCount: row.scriptCount ?? 0,
    briefCount: row.briefCount ?? 0,
    sentCount: row.sentCount ?? 0,
    counts: null,
  };
}

/**
 * Tab badge counts (KTD11):
 * - `opportunities` = every non-deleted opportunity (all statuses)
 * - `scripts` = persona script entity count (`detail.set.scriptCount`)
 * - `sent_to_vendor` = 0 here; the workspace overwrites it from marketplace orders for the set
 */
export function countPersonaOpportunityTabs(
  detail: PersonaOpportunitySetDetail,
): PersonaOpportunityTabCounts {
  const opportunities = detail.personas.reduce(
    (total, persona) => total + persona.opportunities.length,
    0,
  );

  return {
    opportunities,
    scripts: detail.set.scriptCount ?? 0,
    sent_to_vendor: 0,
  };
}

function countDetailOpportunities(detail: PersonaOpportunitySetDetail): number {
  return detail.personas.reduce(
    (total, persona) => total + persona.opportunities.length,
    0,
  );
}

export function toPersonaOpportunitySetFromDetail(
  detail: PersonaOpportunitySetDetail,
): PersonaOpportunitySet {
  return {
    ...toSetBase(detail.set),
    opportunityCount: detail.set.opportunityCount ?? countDetailOpportunities(detail),
    scriptCount: detail.set.scriptCount ?? 0,
    briefCount: detail.set.briefCount ?? 0,
    counts: countPersonaOpportunityTabs(detail),
  };
}

export function personaSetCanOfferRetry(set: { type: string }): boolean {
  return set.type !== "USER_GENERATED" && set.type !== "POST_FLIGHT";
}

export function personaSetCanOfferRegenerate(set: { type: string }): boolean {
  return set.type === "PERSONA";
}
